import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import {
  HandCoins, TrendingUp, TrendingDown, Calendar as CalIcon, Wallet,
  ExternalLink, AlertCircle, RefreshCcw, Inbox, Users, Database,
} from "lucide-react";
import { toast } from "sonner";

const BRAND = {
  terracotta: "#B2674A",
  sage: "#AEBDB1",
  gold: "#C69E5F",
  primary: "#425252",
  peach: "#DCA591",
};

const SLICE_COLORS = [
  BRAND.terracotta, BRAND.gold, BRAND.sage, BRAND.peach, BRAND.primary, "#7A8E8A",
];

const RANGE_OPTIONS = [
  { key: "this_week",  label: "This Week",     prevLabel: "vs last week" },
  { key: "this_month", label: "This Month",    prevLabel: "vs last month" },
  { key: "last_month", label: "Last Month",    prevLabel: "vs prev month" },
  { key: "ytd",        label: "Year-to-Date",  prevLabel: null },  // YoY needs prior-year fetch
];

const currency = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function relativeTime(epochSec) {
  if (!epochSec) return null;
  const diffMs = Date.now() - epochSec * 1000;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 minute ago";
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs === 1) return "1 hour ago";
  if (hrs < 24) return `${hrs} hours ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

export default function Giving() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [backfilling, setBackfilling] = useState(false);
  const runBackfill = async () => {
    if (backfilling) return;
    const year = new Date().getFullYear();
    const ok = window.confirm(
      `Backfill Pushpay weekly history for ${year}?\n\nManual KPI rows are preserved; only auto-imported rows are refreshed.`
    );
    if (!ok) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/giving/backfill", { year });
      if (data.ok) {
        toast.success(
          `${year} backfill complete: ${data.inserted || 0} added, ${data.updated || 0} updated, ${data.skipped_manual || 0} manual preserved`
        );
        await load();
      } else {
        toast.error("Backfill failed: " + (data.error || "unknown"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [range, setRange] = useState("this_month");
  // Re-render the "X minutes ago" label every 30s without a network call.
  const [, setNow] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNow((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/giving/summary");
      setData(data);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse" data-testid="giving-loading">
        <div className="h-12 w-72 rounded-md bg-brand-primary/5" />
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-28 rounded-2xl bg-white border border-brand-primary/10 shadow-warm" />
          ))}
        </div>
        <div className="h-36 rounded-2xl bg-white border border-brand-primary/10 shadow-warm" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-72 rounded-2xl bg-white border border-brand-primary/10 shadow-warm" />
          ))}
        </div>
      </div>
    );
  }

  const connected = !!data?.connected;
  const isLive = data?.source === "pushpay";
  const ranges = data?.ranges || {};
  const trend = data?.trend_12mo || [];
  const errorMsg = data?.error;
  const fetchedAt = data?.fetched_at;
  const fetchedAgo = relativeTime(fetchedAt);

  const rangeMeta = RANGE_OPTIONS.find((r) => r.key === range);
  const currentRange = ranges[range] || { total: 0, count: 0, by_fund: [], by_type: [], delta_pct: null, prev_total: null };

  const tw = ranges.this_week?.total ?? 0;
  const lw = ranges.last_week?.total ?? 0;
  const tm = ranges.this_month?.total ?? 0;
  const lm = ranges.last_month?.total ?? 0;
  const momPct = data?.mom_pct;

  return (
    <div className="space-y-8" data-testid="giving-dashboard">
      {/* Connection / state banner */}
      {!connected && (
        <Card data-testid="pushpay-not-connected-banner" className="rounded-2xl bg-peach/20 border border-terracotta/20 shadow-warm p-5 flex items-start gap-4">
          <AlertCircle className="h-5 w-5 text-terracotta mt-0.5" strokeWidth={1.8} />
          <div className="flex-1">
            <p className="font-medium text-brand-primary">Pushpay is not connected</p>
            <p className="text-sm text-brand-primary/70 mt-1">
              Add the <code className="font-mono">PUSHPAY_*</code> environment variables in deployment settings, or connect via{" "}
              <a href="/settings" className="text-terracotta hover:underline">Settings → Integrations</a> to show real giving data.
            </p>
          </div>
        </Card>
      )}
      {connected && errorMsg && (
        <Card data-testid="pushpay-error-banner" className="rounded-2xl bg-peach/20 border border-terracotta/20 shadow-warm p-5 flex items-start gap-4">
          <AlertCircle className="h-5 w-5 text-terracotta mt-0.5" strokeWidth={1.8} />
          <div className="flex-1">
            <p className="font-medium text-brand-primary">Couldn't reach Pushpay</p>
            <p className="text-sm text-brand-primary/70 mt-1 break-all">{errorMsg}</p>
          </div>
        </Card>
      )}

      {/* Headline + last updated + refresh */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">Giving</p>
          <h2 className="font-heading text-3xl font-semibold text-brand-primary mt-1">Financial visibility</h2>
          <p className="text-sm text-brand-primary/60 mt-1">
            {isLive ? "Live Pushpay data" : "Pushpay placeholder — connect Pushpay for real numbers."}
            {isLive && fetchedAgo && (
              <span data-testid="giving-last-updated" className="ml-2 text-brand-primary/50">
                · Last updated {fetchedAgo}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={runBackfill}
              disabled={backfilling}
              data-testid="giving-backfill-btn"
              className="inline-flex items-center gap-2 rounded-full border border-brand-primary/15 bg-white text-brand-primary text-sm px-4 py-2 hover:bg-brand-primary/5 transition-colors disabled:opacity-50"
              title="Import historical weekly totals from Pushpay (idempotent — manual rows are preserved)"
            >
              <Database className={`h-4 w-4 ${backfilling ? "animate-pulse" : ""}`} />
              {backfilling ? "Backfilling…" : "Backfill history"}
            </button>
          )}
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="giving-refresh-btn"
            className="inline-flex items-center gap-2 rounded-full border border-brand-primary/15 bg-white text-brand-primary text-sm px-4 py-2 hover:bg-brand-primary/5 transition-colors disabled:opacity-50"
          >
            <RefreshCcw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Top stats — This Week / Last Week / This Month / Last Month / MoM / Giving Units */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 sm:gap-5">
        <StatCard testid="giving-stat-this-week"  label="This Week"  value={currency(tw)} icon={HandCoins} color={BRAND.terracotta} />
        <StatCard testid="giving-stat-last-week"  label="Last Week"  value={currency(lw)} icon={CalIcon}    color={BRAND.peach} />
        <StatCard testid="giving-stat-this-month" label="This Month" value={currency(tm)} icon={CalIcon}    color={BRAND.gold} />
        <StatCard testid="giving-stat-last-month" label="Last Month" value={currency(lm)} icon={Wallet}     color={BRAND.primary} />
        <StatCard
          testid="giving-stat-mom"
          label="MoM Change"
          value={momPct == null ? "—" : `${momPct >= 0 ? "+" : ""}${momPct.toFixed(1)}%`}
          icon={momPct == null ? TrendingUp : (momPct >= 0 ? TrendingUp : TrendingDown)}
          color={momPct == null ? BRAND.sage : (momPct >= 0 ? BRAND.sage : BRAND.terracotta)}
        />
        <UnitsStatCard
          testid="giving-stat-units-mtd"
          units={ranges.this_month?.units ?? 0}
          deltaPct={ranges.this_month?.units_delta_pct}
        />
      </div>

      {/* Range toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">Showing</p>
        <div data-testid="giving-range-toggle" className="inline-flex rounded-full bg-white border border-brand-primary/10 shadow-warm p-1">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`giving-range-${r.key}`}
              className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm rounded-full transition-colors ${
                range === r.key
                  ? "bg-terracotta text-white"
                  : "text-brand-primary/70 hover:text-brand-primary"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Prominent total header */}
      <TotalGivingHeader
        rangeLabel={rangeMeta.label}
        prevLabel={rangeMeta.prevLabel}
        total={currentRange.total}
        count={currentRange.count}
        deltaPct={currentRange.delta_pct}
        prevTotal={currentRange.prev_total}
      />

      {/* Fund + Type breakdowns + Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <BreakdownCard
          testid="giving-by-fund"
          title="By fund"
          subtitle="Where the gifts are designated"
          rows={currentRange.by_fund}
          rangeLabel={rangeMeta.label}
          prevLabel={rangeMeta.prevLabel}
        />
        <BreakdownCard
          testid="giving-by-type"
          title="By payment type"
          subtitle="Digital vs. in-person"
          rows={currentRange.by_type}
          rangeLabel={rangeMeta.label}
          prevLabel={rangeMeta.prevLabel}
        />

        {/* Monthly trend */}
        <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6" data-testid="giving-trend-chart">
          <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">12-month trend</p>
          <h3 className="font-heading text-xl text-brand-primary mt-1 mb-4">Total giving over time</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="givingTrendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BRAND.terracotta} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={BRAND.terracotta} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#42525210" vertical={false} />
                <XAxis dataKey="month" stroke="#42525266" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis
                  stroke="#42525266"
                  fontSize={11}
                  tickFormatter={(v) => `$${Math.round(v / 1000)}k`}
                  width={48}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  cursor={{ stroke: "#B2674A", strokeWidth: 1, strokeDasharray: "3 3" }}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #4252521a",
                    boxShadow: "0 8px 30px rgba(178,103,74,0.12)",
                    fontSize: 13,
                    padding: "8px 12px",
                  }}
                  labelStyle={{ color: "#425252", fontWeight: 600, marginBottom: 2 }}
                  formatter={(v) => [currency(v), "Giving"]}
                />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke={BRAND.terracotta}
                  strokeWidth={2.5}
                  fill="url(#givingTrendFill)"
                  dot={{ r: 3, fill: BRAND.terracotta, strokeWidth: 0 }}
                  activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* CTA */}
      <Card className="rounded-2xl bg-brand-primary text-white p-8 shadow-warm-lg flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-peach">Pushpay Portal</p>
          <p className="font-heading text-2xl mt-1">Manage donors, funds, and campaigns</p>
          <p className="text-white/75 mt-1 text-sm">Open Pushpay for refunds, donor records, and detailed reporting.</p>
        </div>
        <a
          href="https://pushpay.com"
          target="_blank"
          rel="noreferrer"
          data-testid="open-pushpay-link"
          className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white rounded-full px-6 py-3 font-medium transition-all"
        >
          Open Pushpay <ExternalLink className="h-4 w-4" />
        </a>
      </Card>
    </div>
  );
}

function UnitsStatCard({ testid, units, deltaPct }) {
  const Trend = deltaPct == null ? null : deltaPct >= 0 ? TrendingUp : TrendingDown;
  const trendColor =
    deltaPct == null ? "" : deltaPct >= 0 ? "text-sage" : "text-terracotta";
  return (
    <Card
      data-testid={testid}
      className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-warm-lg"
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: `${BRAND.sage}1A` }}>
          <Users className="h-4 w-4" style={{ color: BRAND.sage }} strokeWidth={1.8} />
        </div>
        <p className="text-xs sm:text-sm text-brand-primary/70">Giving Units MTD</p>
      </div>
      <p className="font-heading text-2xl sm:text-3xl font-light text-brand-primary tracking-tight" data-testid={`${testid}-value`}>
        {Number(units || 0).toLocaleString()}
      </p>
      {Trend && (
        <p className={`inline-flex items-center gap-1 text-xs mt-1.5 ${trendColor}`} data-testid={`${testid}-delta`}>
          <Trend className="h-3 w-3" />
          {deltaPct >= 0 ? "+" : ""}{deltaPct.toFixed(1)}% vs last month
        </p>
      )}
      {!Trend && (
        <p className="text-xs text-brand-primary/45 mt-1.5">unique givers this month</p>
      )}
    </Card>
  );
}

function StatCard({ testid, label, value, icon: Icon, color }) {
  return (
    <Card
      data-testid={testid}
      className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-warm-lg"
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className="h-9 w-9 rounded-xl grid place-items-center" style={{ background: `${color}1A` }}>
          <Icon className="h-4 w-4" style={{ color }} strokeWidth={1.8} />
        </div>
        <p className="text-xs sm:text-sm text-brand-primary/70">{label}</p>
      </div>
      <p className="font-heading text-2xl sm:text-3xl font-light text-brand-primary tracking-tight">
        {value}
      </p>
    </Card>
  );
}

function TotalGivingHeader({ rangeLabel, prevLabel, total, count, deltaPct, prevTotal }) {
  const Trend = deltaPct == null ? null : deltaPct >= 0 ? TrendingUp : TrendingDown;
  const trendColor =
    deltaPct == null ? "" : deltaPct >= 0 ? "text-sage" : "text-terracotta";
  return (
    <Card
      data-testid="giving-total-header"
      className="rounded-2xl bg-gradient-to-br from-cream-100 to-white border border-brand-primary/10 shadow-warm p-6 sm:p-8"
    >
      <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta font-semibold">
        Total giving · {rangeLabel}
      </p>
      <div className="mt-2 flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-baseline gap-4 flex-wrap">
          <p className="font-heading text-5xl sm:text-6xl font-light text-brand-primary tracking-tight" data-testid="giving-total-amount">
            {currency(total)}
          </p>
          <p className="text-sm text-brand-primary/60" data-testid="giving-total-count">
            {count} {count === 1 ? "gift" : "gifts"}
          </p>
        </div>
        {Trend && prevLabel && (
          <div className="text-right">
            <p
              data-testid="giving-total-delta"
              className={`inline-flex items-center gap-1.5 text-base font-medium ${trendColor}`}
            >
              <Trend className="h-5 w-5" />
              {deltaPct >= 0 ? "+" : ""}{deltaPct.toFixed(1)}% {prevLabel}
            </p>
            {prevTotal != null && (
              <p className="text-xs text-brand-primary/50 mt-0.5">
                Previous period: {currency(prevTotal)}
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function BreakdownCard({ testid, title, subtitle, rows, rangeLabel, prevLabel }) {
  const data = useMemo(() => (rows || []).filter((r) => r.amount > 0), [rows]);
  const isEmpty = data.length === 0;

  return (
    <Card data-testid={testid} className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
      <p className="text-[11px] uppercase tracking-[0.18em] text-terracotta font-semibold">{title}</p>
      <h3 className="font-heading text-xl text-brand-primary mt-1 mb-4">{subtitle}</h3>

      {isEmpty ? (
        <div data-testid={`${testid}-empty`} className="rounded-xl border border-dashed border-brand-primary/15 bg-brand-primary/[0.02] p-6 text-center">
          <Inbox className="h-6 w-6 text-brand-primary/40 mx-auto mb-2" strokeWidth={1.6} />
          <p className="text-sm text-brand-primary/65 font-medium">No giving data for {rangeLabel.toLowerCase()}.</p>
          <p className="text-xs text-brand-primary/45 mt-1">Once gifts are recorded in Pushpay, they'll appear here.</p>
        </div>
      ) : (
        <>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="amount" nameKey="name" innerRadius={42} outerRadius={70} paddingAngle={2}>
                  {data.map((_, i) => (
                    <Cell key={i} fill={SLICE_COLORS[i % SLICE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => currency(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2.5 mt-2">
            {data.slice(0, 6).map((r, i) => {
              const showDelta = prevLabel && r.delta_pct != null;
              const Trend = !showDelta ? null : (r.delta_pct >= 0 ? TrendingUp : TrendingDown);
              const trendColor = !showDelta ? "" : (r.delta_pct >= 0 ? "text-sage" : "text-terracotta");
              return (
                <div key={r.name} className="flex items-center justify-between gap-3 text-sm">
                  <span className="inline-flex items-center gap-2 text-brand-primary/85 truncate min-w-0">
                    <span
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ background: SLICE_COLORS[i % SLICE_COLORS.length] }}
                    />
                    <span className="truncate">{r.name}</span>
                  </span>
                  <span className="text-right shrink-0 leading-tight">
                    <span className="font-medium text-brand-primary block">
                      {currency(r.amount)} <span className="text-brand-primary/50 ml-1 text-xs font-normal">· {r.pct}%</span>
                    </span>
                    {showDelta ? (
                      <span className={`inline-flex items-center gap-0.5 text-[11px] mt-0.5 ${trendColor}`}>
                        <Trend className="h-3 w-3" />
                        {r.delta_pct >= 0 ? "+" : ""}{r.delta_pct.toFixed(1)}% {prevLabel}
                      </span>
                    ) : (prevLabel && r.prev_amount === 0) ? (
                      <span className="text-[11px] text-brand-primary/45 mt-0.5 block">new this period</span>
                    ) : null}
                  </span>
                </div>
              );
            })}
            {data.length > 6 && (
              <p className="text-xs text-brand-primary/45 italic mt-1">+ {data.length - 6} more</p>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
